import { BatteryMedium, Clock, Radio, Sun } from "lucide-react";
import { FieldMetric } from "components/field/FieldLayout";
import { fieldTime } from "utils/fieldFormat";
import { onCellular } from "utils/radarDiagnostics";

// Status on the node page: when the node last reported and on which
// firmware and radio, its battery and the sun now, and when it plans to wake
// and upload next (its newest reading, as sent). Firmware 0.3.7 also counts
// today's charge itself (`e`); then that is shown too.
const BANDS = ["the normal rate", "half rate (3.10-3.20 V)", "quarter rate (3.00-3.10 V)"];
const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
export const epochTime = (s) => (num(s) && s > 1.6e9 ? `${fieldTime(new Date(s * 1000).toISOString())} SGT` : "–");

export function ago(iso, now = Date.now()) {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return "";
  const min = Math.max(0, Math.round((now - t) / 60000));
  if (min < 60) return `${min} min ago`;
  if (min < 48 * 60) return `${Math.round(min / 60)} h ago`;
  return `${Math.round(min / 1440)} days ago`;
}

export default function NodeStatusCard({ sample, now = Date.now() }) {
  const raw = sample?.raw || {};
  const bv = num(raw.bv) ?? num(sample?.battery_voltage);
  const band = num(raw.batt_band);
  const si = num(raw.si);
  const sv = num(raw.sv);
  const e = raw.e && typeof raw.e === "object" ? raw.e : null;
  return (
    <section className="field-panel" aria-label="Status">
      <div className="field-panel-head">
        <div>
          <h2>Status</h2>
          <p>Latest reading</p>
        </div>
        <span className="field-state">{sample ? ago(sample.timestamp, now) : "no reading yet"}</span>
      </div>
      <div className="field-panel-body">
        {!sample ? (
          <div className="field-notice">This node has not reported yet.</div>
        ) : (
          <>
            <div className="field-metrics field-node-metrics">
              <FieldMetric
                icon={Clock}
                label="Last report"
                value={`${fieldTime(sample.timestamp)} SGT`}
                detail={`Firmware ${raw.fw || raw.sha8 || "unknown"}`}
              />
              <FieldMetric
                icon={Radio}
                label="Radio"
                value={onCellular(raw) ? "Cellular" : "Wi-Fi"}
                detail={num(raw.rssi) !== null ? `Signal ${raw.rssi} dBm` : null}
              />
              <FieldMetric
                icon={BatteryMedium}
                label="Battery"
                value={bv !== null ? `${bv.toFixed(3)} V` : "–"}
                detail={band !== null ? `Wakes at ${BANDS[band] || `band ${band}`}` : null}
              />
              <FieldMetric
                icon={Sun}
                label="Sun now"
                value={si !== null ? `${Math.round(si * 1000)} mA` : "–"}
                detail={sv !== null ? `Panel ${sv.toFixed(2)} V` : null}
              />
            </div>
            <p className="field-caption">
              Next wake {epochTime(raw.next_wake)} · next upload {epochTime(raw.next_send ?? raw.next_wake)}
              {num(raw.queue_n) !== null ? ` · ${raw.queue_n} readings queued` : ""}.
              {e && num(e.use) !== null
                ? ` Today: ${Math.round(e.use)} mAh used, ${Math.round(num(e.sol) ?? 0)} mAh charged.`
                : ""}
            </p>
          </>
        )}
      </div>
    </section>
  );
}
